// TNG-style warp drive effect, adapted from o2bomb/space-warp.
//
// Technique: an InstancedMesh of small spheres distributed in a box
// around the camera, where each instance is stretched along its
// local Z axis proportional to warp velocity. The instances stream
// past the camera in +Z (camera-local back), wrap when they exit,
// and fade in colour as they approach the camera position from the
// front. Bloom in the existing post-processing pipeline turns each
// stretched sphere into a bright light-streak.
//
// Reference: https://github.com/o2bomb/space-warp
// The original uses a one-shot decay velocity for a "jump to warp"
// burst. We feed a SUSTAINED warp factor (0..1) so the streaks
// hold at warp 9.9999 indefinitely instead of decaying out.

import * as THREE from 'three';

const COUNT = 1400;            // dense TNG-grade starfield. With the 70×70×50
                               // volume below, density lands at ~0.006 stars/unit³
                               // (avg spacing ~5.6 units) — enough streaks to
                               // fill the view without overlapping at max warp.
const XY_BOUNDS = 70;          // wider lateral spread so stars cover the full
                               // view angle, not just a tight column
const Z_BOUNDS = 50;           // deeper streak corridor — streaks have more
                               // time on-screen before wrapping
const MAX_SPEED_FACTOR = 0.5;  // slower forward motion so streaks linger (TNG feel)
const MAX_SCALE_FACTOR = 85;   // peak streak length multiplier — sphere of
                               // radius 0.018 × 85 = ~1.53-unit streak at max warp
const SCALE_CURVE_EXP = 1.35;  // power curve on the velocity → scaleZ map.
                               // Higher = more dramatic ramp (low warp shorter,
                               // high warp longer relative to a linear ramp).

const BASE_SPHERE_RADIUS = 0.018; // tiny seed point; bloom inflates the visual
const TAIL_FADE_MIN = 0.05;       // brightness floor at the tail end (5%)

export function createWarpEffect() {
  // 8×6 segments smooths the ellipsoid silhouette once it's stretched
  // 65×, which is what stops the streaks reading as octahedral lines
  // at high warp.
  const geometry = new THREE.SphereGeometry(BASE_SPHERE_RADIUS, 8, 6);
  // Bright "over-1" color so the streaks pile into the bloom pass.
  // Slight blue lift (B > G > R) hints the cool Doppler tint of the
  // TNG warp aesthetic. toneMapped: false keeps the > 1.0 values
  // intact rather than ACES-clamping them to grey before bloom samples.
  const material = new THREE.MeshBasicMaterial({
    color: new THREE.Color(1.3, 1.45, 1.7),
    toneMapped: false
  });
  // Comet-tail brightness fade. Local +Z on the geometry is the streak
  // HEAD (where the star is heading) and local -Z is the TAIL (where
  // it came from). Brightness ramps from TAIL_FADE_MIN at the tail
  // tip to 1.0 at the head, with a pow(1.6) curve so the head reads
  // as a bright point and most of the tail glows soft. Injected via
  // onBeforeCompile rather than a full custom ShaderMaterial so the
  // instanceColor + instanceMatrix paths Three.js wires up
  // automatically for InstancedMesh still work without manual setup.
  material.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader.replace(
      '#include <common>',
      `#include <common>\nvarying float vLocalZ;`
    );
    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>\n  vLocalZ = position.z;`
    );
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <common>',
      `#include <common>\nvarying float vLocalZ;`
    );
    shader.fragmentShader = shader.fragmentShader.replace(
      'vec4 diffuseColor = vec4( diffuse, opacity );',
      `vec4 diffuseColor = vec4( diffuse, opacity );
      float zNorm = (vLocalZ + ${BASE_SPHERE_RADIUS.toFixed(4)}) / ${(BASE_SPHERE_RADIUS * 2).toFixed(4)};
      float tailFade = mix(${TAIL_FADE_MIN.toFixed(4)}, 1.0, pow(clamp(zNorm, 0.0, 1.0), 1.6));
      diffuseColor.rgb *= tailFade;`
    );
  };
  const mesh = new THREE.InstancedMesh(geometry, material, COUNT);
  mesh.frustumCulled = false; // mesh follows camera, custom frustum behaviour
  mesh.matrixAutoUpdate = true;
  mesh.renderOrder = 100;
  mesh.visible = false;

  // Seed instance positions in a uniform box around camera. setColorAt
  // lazily allocates instanceColor, so call it once during init to
  // make sure the buffer exists before the animation loop touches it.
  const initObj = new THREE.Object3D();
  const initColor = new THREE.Color(1, 1, 1);
  for (let i = 0; i < COUNT; i++) {
    initObj.position.set(
      (Math.random() - 0.5) * XY_BOUNDS,
      (Math.random() - 0.5) * XY_BOUNDS,
      (Math.random() - 0.5) * Z_BOUNDS
    );
    initObj.scale.set(1, 1, 1);
    initObj.updateMatrix();
    mesh.setMatrixAt(i, initObj.matrix);
    mesh.setColorAt(i, initColor);
  }
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;

  // Scratch objects reused per frame so the inner loop doesn't allocate.
  const tempMatrix = new THREE.Matrix4();
  const tempPos = new THREE.Vector3();
  const updateObj = new THREE.Object3D();
  const updateColor = new THREE.Color();

  return {
    mesh,
    /**
     * Drive the warp visual from outside. The caller is responsible
     * for adding `mesh` to the scene once at init; this update copies
     * the camera's position + quaternion onto the mesh each frame so
     * the warp field stays anchored to the active viewpoint even
     * though it lives at the scene root rather than as a camera child.
     *
     * @param {THREE.Camera} camera live camera so the field tracks it
     * @param {number} warpFactor 0..1 normalized warp intensity
     *   (0 = no warp, 1 = max). Typically driven from
     *   speedToWarpFactor() mapped onto [0, 1] via
     *   (warp - 1) / 8.9999.
     * @param {number} delta frame delta in seconds
     */
    setWarpFactor(camera, warpFactor, delta) {
      const visible = warpFactor > 0.01;
      mesh.visible = visible;
      if (!visible) return;

      // Sync mesh transform to the camera so the streak field always
      // wraps the viewpoint.
      mesh.position.copy(camera.position);
      mesh.quaternion.copy(camera.quaternion);

      // Sustained velocity proportional to warp factor. The reference
      // implementation uses 1/(x^x) for a one-shot decay; we keep it
      // constant so warp cruise produces continuous streaks.
      const velocity = warpFactor;
      const speedStep = Math.max(delta, velocity * MAX_SPEED_FACTOR);
      // Power curve on streak length: low warp keeps streaks short
      // ("just into warp" reads as subtle motion blur), high warp
      // grows them dramatically so warp 9.9999 reads as proper
      // line-streaks rather than chunky ellipsoids. With exponent
      // 1.35 the ramp is:
      //   warp 5  (v≈0.44) → scaleZ ≈ 28
      //   warp 9  (v≈0.89) → scaleZ ≈ 72
      //   warp 9.9999      → scaleZ ≈ 85 (max)
      const scaleZ = Math.max(1, Math.pow(velocity, SCALE_CURVE_EXP) * MAX_SCALE_FACTOR);
      // Lateral scale narrows with warp factor so the stretched
      // sphere reads as a thin TNG light-streak rather than a fat
      // ellipsoid. At warp 0 each star is a 1× sphere; at max warp
      // lateral scale is 0.5, giving ~170:1 length-to-width streaks.
      const scaleXY = 1 - 0.5 * velocity;

      for (let i = 0; i < COUNT; i++) {
        mesh.getMatrixAt(i, tempMatrix);
        tempPos.setFromMatrixPosition(tempMatrix);

        // Stream toward camera-back (+Z in camera-local). Wrap when
        // an instance has passed the camera by Z_BOUNDS/2 so it
        // recycles to the far-front for another pass.
        if (tempPos.z > Z_BOUNDS / 2) {
          tempPos.z = -Z_BOUNDS / 2;
        } else {
          tempPos.z += speedStep;
        }

        updateObj.position.copy(tempPos);
        updateObj.scale.set(scaleXY, scaleXY, scaleZ);
        updateObj.updateMatrix();
        mesh.setMatrixAt(i, updateObj.matrix);

        // Streaks at the camera (z ≈ 0) and behind (z > 0) read at
        // full brightness; ahead-of-camera instances fade from white
        // at z=0 toward black at z=-Z_BOUNDS/2 so the stars appear
        // to materialise out of the deep-field rather than popping
        // in fully lit.
        const colorIntensity = tempPos.z > 0
          ? 1
          : 1 - Math.min(1, -tempPos.z / (Z_BOUNDS / 2));
        updateColor.setRGB(colorIntensity, colorIntensity, colorIntensity);
        mesh.setColorAt(i, updateColor);
      }
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    },
    dispose() {
      geometry.dispose();
      material.dispose();
      if (mesh.parent) mesh.parent.remove(mesh);
    }
  };
}
